import { useEffect, useRef, useState } from "react";
import intlTelInput from "intl-tel-input";
import type { Iso2 } from "intl-tel-input";
import "intl-tel-input/styles";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface PhoneData {
  number: string;
  dialCode: string;
  country: string;
  iso2: string;
  valid: boolean;
}

// Detect the visitor's country from their IP so the phone field's country
// selector defaults to where they're connecting from — same approach used
// across our other landing pages. The promise is cached at module scope so the
// three PhoneField instances (mobile / desktop / dialog cards, all sharing one
// form) trigger the network request only once per page load.
const IP_COUNTRY_LOOKUP_URL =
  "https://api.ipinfo.io/lite/me?token=84e0007721876e";

let ipCountryPromise: Promise<string | null> | null = null;

function lookupIpCountry(): Promise<string | null> {
  if (!ipCountryPromise) {
    ipCountryPromise = fetch(IP_COUNTRY_LOOKUP_URL)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) =>
        data && typeof data.country_code === "string"
          ? data.country_code
          : null,
      )
      .catch(() => null);
  }
  return ipCountryPromise;
}

// intl-tel-input only kicks off its `loadUtils` import once the document is fully
// loaded (readyState === "complete", i.e. AFTER window.load — which itself waits
// for every image). These landing pages carry 20+ MB of imagery, so on a cold
// visit the utils bundle — and with it the example-number placeholder ("mask") —
// is delayed for seconds; a refresh serves the images from cache, which is the
// reason the mask appeared to "only work after refreshing". Start the utils
// import ourselves at module load so it races the images instead of waiting for
// them. attachUtils dedupes globally (startedLoadingUtils), so the per-instance
// `loadUtils` option below becomes a harmless no-op.
void intlTelInput
  .attachUtils(() => import("intl-tel-input/utils"))
  .catch(() => {});

export function PhoneField({
  defaultCountry = "gb",
  invalid = false,
  className = "",
  placeholder = "Phone Number",
  initialValue = "",
  onChange,
  onBlur,
}: {
  defaultCountry?: string;
  invalid?: boolean;
  className?: string;
  placeholder?: string;
  initialValue?: string;
  onChange: (data: PhoneData) => void;
  onBlur?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const initialValueRef = useRef(initialValue);
  const itiRef = useRef<ReturnType<typeof intlTelInput> | null>(null);
  // True while we are programmatically calling setNumber() to sync from the
  // shared form, so the emit() handler doesn't echo the change straight back.
  const syncingRef = useRef(false);
  // intl-tel-input's utils bundle loads async; getNumber()/isValidNumber()
  // THROW until it's ready, so the sync effect must wait for this flag.
  const [utilsReady, setUtilsReady] = useState(false);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    let mounted = true;

    const iti = intlTelInput(el, {
      // Pre-select the country from the visitor's IP, falling back to
      // `defaultCountry` when detection is unavailable or slow. Raced against a
      // short timeout so a flag always appears promptly, and always resolves to
      // a valid iso2 so intl-tel-input never lands in its no-country state.
      // Automatically ignored once a number with a dial code is set (see below).
      initialCountryLookup: () =>
        Promise.race([
          lookupIpCountry(),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
        ]).then((iso2) => (iso2 || defaultCountry) as Iso2),
      separateDialCode: true,
      // Show a country-appropriate example number as the placeholder (the
      // format "mask" hint). "AGGRESSIVE" is required rather than the default
      // "POLITE" because we pass a static `placeholder` below, which sets
      // intl-tel-input's hadInitialPlaceholder flag and would otherwise suppress
      // the auto-placeholder entirely. This also covers first load: once the
      // utils bundle loads and the IP-detected country resolves, intl-tel-input
      // refreshes the placeholder to that country's format — so the mask matches
      // the default country, not just countries the visitor picks manually.
      placeholderNumberPolicy: "AGGRESSIVE",
      countryOrder: ["gb", "us", "tr", "ae", "sa", "de", "fr"],
      loadUtils: () => import("intl-tel-input/utils"),
    });
    itiRef.current = iti;

    const emit = () => {
      if (syncingRef.current) return;
      const data = iti.getSelectedCountry();
      // getNumber()/isValidNumber() throw until the utils bundle loads, and a
      // country change from IP detection can arrive before that — so read them
      // defensively. The promise handler below re-emits once utils are ready.
      let number = "";
      let valid = false;
      try {
        number = iti.getNumber() || "";
        valid = iti.isValidNumber() === true;
      } catch {
        /* utils not loaded yet */
      }
      onChangeRef.current({
        number,
        dialCode: data && data.dialCode ? "+" + data.dialCode : "",
        country: data && data.name ? data.name : "",
        iso2: data && data.iso2 ? data.iso2 : "",
        valid,
      });
    };

    // Hydrate from any existing form value so switching between the mobile/
    // desktop/dialog cards (which share one form) preserves what was typed.
    if (initialValueRef.current) {
      iti.setNumber(initialValueRef.current);
    }

    // Clear any typed digits when the visitor actively picks a different country
    // from the dropdown: the leftover value belongs to the previous country's
    // format, hides the placeholder, and reads as invalid for the new country.
    // Wiping it reveals the new country's example-number mask and lets them
    // re-enter cleanly. The `open:countryselector` flag scopes this to genuine
    // dropdown selections — it must NOT fire for programmatic setNumber()
    // (initialValue hydration / cross-card sync) or the IP-based initial country,
    // all of which need their value preserved.
    let userOpenedSelector = false;
    const onOpenSelector = () => {
      userOpenedSelector = true;
    };
    const onCountryChange = () => {
      if (userOpenedSelector) {
        userOpenedSelector = false;
        if (el.value) el.value = "";
      }
      emit();
    };

    el.addEventListener("input", emit);
    el.addEventListener("open:countryselector", onOpenSelector);
    el.addEventListener("countrychange", onCountryChange);

    // Once utils load, refresh validity — but ONLY when this field actually
    // holds a value, so a freshly-mounted empty instance never overwrites the
    // shared form's phone data with blanks.
    iti.promise
      .then(() => {
        if (!mounted) return;
        setUtilsReady(true);
        if (el.value) emit();
      })
      .catch(() => {});

    return () => {
      mounted = false;
      el.removeEventListener("input", emit);
      el.removeEventListener("open:countryselector", onOpenSelector);
      el.removeEventListener("countrychange", onCountryChange);
      iti.destroy();
      itiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep this instance in sync with the shared form value. Because the mobile /
  // desktop / dialog cards mount simultaneously and bind to ONE form, a value
  // typed in one must show up in the others (and a stale instance must never
  // clobber the form on interaction). When the external value changes, push it
  // into intl-tel-input — guarded so emit() doesn't echo it back into a loop.
  useEffect(() => {
    const iti = itiRef.current;
    // getNumber() throws until the utils bundle is loaded — wait for it.
    if (!iti || !utilsReady) return;
    const current = iti.getNumber() || "";
    if (initialValue === current) return;
    syncingRef.current = true;
    iti.setNumber(initialValue || "");
    syncingRef.current = false;
  }, [initialValue, utilsReady]);

  // Wrap in a div React fully owns: intl-tel-input moves the <input> into its
  // own `.iti` wrapper, so without this React's unmount can throw "removeChild:
  // node is not a child" (e.g. when the consultation dialog closes). React only
  // ever removes this outer div, leaving intl-tel-input's mutations untouched.
  return (
    <div>
      <Input
        ref={inputRef}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder={placeholder}
        onBlur={onBlur}
        className={cn(className, invalid && "border-destructive")}
      />
    </div>
  );
}
